﻿import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { DateInput, TimePrecision } from "@blueprintjs/datetime";
import { Button } from "components/buttons";
import { FormHelperText } from "components/form-fields";
import classNames from "classnames";
import moment from "moment";
import 'components/form-fields/FormFields.css';
import "moment/locale/en-gb";
import { enGB } from "date-fns/locale";
import { ShowHide } from "components/layout";

const dateLocale = "en-gb";
const dateFormat = "DD/MM/YYYY";
const timeFormat = "HH:mm";

export function FormDateInput(
    {
        id = null,
        value = null,
        disabled = false,
        loading = false,
        minDate = null,
        maxDate = null,
        headingText = null,
        dangerHelperText = "",
        helperText = "",
        modifiers = null,
        includeTime = false,
        defaultToNowWhenEmpty = false,
        fill = false,
        firstDayOfWeek = 1,
        onChange,
        onError = null
    }
) {
    const [valueValue, setValue] = useState(null);
    const monthsInPastDefault = 30;
    const yearsInFutureDefault = 5;
    const minDateDefault = moment().subtract(monthsInPastDefault, 'months').toDate();
    const maxDateDefault = moment().add(yearsInFutureDefault, 'years').toDate();
    const [minDateValue, setMinDate] = useState(minDateDefault);
    const [maxDateValue, setMaxDate] = useState(maxDateDefault);
    const inputRef = useRef(null);
    const [timeSettings, setTimeSettings] = useState({});
    const [dateFormatString, setDateFormatString] = useState("DD/MM/YYYY");


    useEffect(() => {
        moment.updateLocale(dateLocale, {
            week: {
                dow: firstDayOfWeek, // defaults to monday
            }
        });

    }, [firstDayOfWeek]);

    useEffect(() => {
        // Blueprint 6's DateInput expects an ISO string for `value`, not a Date object.
        // Passing a Date causes the input to render empty.
        var newValue = null;

        if (value) {
            if (includeTime) {
                newValue = moment.utc(value).locale(dateLocale).toISOString();
            } else {
                newValue = moment(value).locale(dateLocale).toISOString();
            }
        }

        setValue(newValue);

    }, [value, includeTime]);

    useEffect(() => {
        setTimeSettings(includeTime ? { timePrecision: TimePrecision.MINUTE, showTimezoneSelect: false, timePickerProps: { showArrowButtons: true, onChange: handleDateChange, selectAllOnFocus: true }, closeOnSelection: false } : {});
        setDateFormatString(includeTime ? `${dateFormat} ${timeFormat}` : dateFormat);
    }, [includeTime])

    function setMinDateFromProps() {
        const newMinDate = minDate || minDateDefault;
        setMinDate(newMinDate);
    }
    function setMaxDateFromProps() {
        const newMaxDate = maxDate || maxDateDefault;
        setMaxDate(newMaxDate);
    }

    function handleDateChange(selectedDate, isUserChange) {

        if (!isUserChange) {
            return;
        }

        if (selectedDate === "") {
            selectedDate = null;
        }
        onChange(selectedDate);
    }

    // Selects the current date and time as soon as the picker is opened on an empty field, so a
    // "right now" value can be given with a single click instead of being typed. Wired to the mouse
    // press as well as the popover opening: the underlying input keeps its own copy of the text while
    // it holds focus, so only a value arriving before the field takes focus shows in the box.
    function selectNowWhenEmpty() {
        if (!defaultToNowWhenEmpty || value) {
            return;
        }

        onChange(moment().toDate());
    }

    function openCalendar() {
        if (inputRef.current != null) {
            inputRef.current.focus();
        }
    }

    // Blueprint opens an empty picker on the midpoint between the earliest and latest selectable dates
    // whenever today falls outside them, which lands on a month unrelated to the field. The nearest date
    // the field will actually accept is the useful place to start from instead. A picker holding a value
    // is left alone, because Blueprint opens that on the value and an initial month would override it.
    function getInitialMonth() {
        if (valueValue != null) {
            return undefined;
        }

        const today = new Date();

        if (today < minDateValue) {
            return minDateValue;
        }

        if (today > maxDateValue) {
            return maxDateValue;
        }

        return undefined;
    }

    function receiveInputRef(ref) {
        inputRef.current = ref;
    }

    
    useEffect(setMinDateFromProps, [minDate]);
    useEffect(setMaxDateFromProps, [maxDate]);

    return (
        <div className={classNames("form-field", "form-field-date-input", { "fill": fill })}>
            <ShowHide
                evaluator={headingText != null}
                show={(
                    <h4 className={classNames({ "bp6-skeleton": loading })}>{headingText}</h4>
                )}
            />
            <FormHelperText loading={loading}>{helperText}</FormHelperText>
            <DateInput
                disabled={disabled}
                className={classNames({ "fill": fill })}
                formatDate={date => moment(date).locale(dateLocale).format(dateFormatString)}
                parseDate={str => moment(str, dateFormatString).locale(dateLocale).toDate()}
                placeholder={dateFormatString}
                value={valueValue}
                onChange={handleDateChange}
                onError={onError ?? undefined}
                popoverProps={{ placement: "bottom", usePortal: true, onOpening: selectNowWhenEmpty }}
                inputRef={inputRef}
                inputProps={{
                    id: id,
                    className: classNames(includeTime ? "medium" : "small", { "bp6-skeleton": loading }),
                    inputRef: receiveInputRef,
                    onMouseDown: selectNowWhenEmpty,
                }}
                rightElement={(
                    <Button
                        icon="calendar"
                        minimal={true}
                        onClick={openCalendar}
                    />
                )}
                minDate={minDateValue}
                maxDate={maxDateValue}
                initialMonth={getInitialMonth()}
                dayPickerProps={modifiers?.disabledDays != null ? { disabled: modifiers.disabledDays } : undefined}
                locale={enGB}
                {...timeSettings}
            ></DateInput>
            <FormHelperText loading={loading} danger={true}>{dangerHelperText}</FormHelperText>
        </div>
    );
}

FormDateInput.propTypes = {
    id: PropTypes.string,
    value: PropTypes.string,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    loading: PropTypes.bool,
    headingText: PropTypes.string,
    disabled: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    // Called with the rejected date, in place of onChange, when a typed date is invalid or out of range.
    onError: PropTypes.func,
    modifiers: PropTypes.object,
    dangerHelperText: PropTypes.string,
    helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    includeTime: PropTypes.bool,
    defaultToNowWhenEmpty: PropTypes.bool,
    fill: PropTypes.bool,
    firstDayOfWeek: PropTypes.number //This must be set consitently on a page. You cannot have on dateInput starting on different days of the week on the same page 
};